import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Shield, LogOut, Save, Plus, Trash2, Image as ImageIcon,
  Trophy, Inbox, Mail, Briefcase, ExternalLink,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  adminMe, getContent, updateContent, listInquiries,
} from "../lib/api";
import * as defaults from "../mock/mock";
import { useToast } from "../hooks/use-toast";

const numericKeys = ["sacks", "tackles", "forcedFumbles", "fumbleRecoveries"];

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState({
    bio: defaults.player.bio,
    tagline: defaults.player.tagline,
    heightCurrent: defaults.player.heightCurrent,
    heightProjected: defaults.player.heightProjected,
    weight: defaults.player.weight,
    gpa: defaults.player.gpa,
    classOf: defaults.player.classOf,
    school: defaults.player.school,
    jerseyNumber: defaults.player.jerseyNumber,
    contactEmail: defaults.player.contactEmail,
    recruitingEmail: defaults.player.recruitingEmail,
    seasonLabel: defaults.seasonStats.season,
    sacks: defaults.seasonStats.items[0].value,
    tackles: defaults.seasonStats.items[1].value,
    forcedFumbles: defaults.seasonStats.items[2].value,
    fumbleRecoveries: defaults.seasonStats.items[3].value,
    highlightTitle: defaults.highlightVideo.title,
    highlightYoutubeId: defaults.highlightVideo.youtubeId,
    accolades: defaults.accolades,
    gallery: defaults.gallery.map(({ id, ...rest }) => rest),
  });
  const [inquiries, setInquiries] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        await adminMe();
      } catch {
        navigate("/admin/login");
        return;
      }
      try {
        const c = await getContent();
        setData((prev) => ({
          ...prev,
          ...Object.fromEntries(
            Object.entries(c).filter(([k, v]) => v !== undefined && v !== null && k !== "updatedAt")
          ),
        }));
      } catch {}
      try {
        const inq = await listInquiries();
        setInquiries(inq);
      } catch {}
      setLoading(false);
    })();
  }, [navigate]);

  const set = (k) => (e) => {
    const v = e?.target ? e.target.value : e;
    setData((d) => ({ ...d, [k]: numericKeys.includes(k) ? Number(v || 0) : v }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await updateContent(data);
      toast({ title: "Saved", description: "Your portfolio is updated live." });
    } catch (err) {
      toast({
        title: "Save failed",
        description: err?.response?.data?.detail || "Try again.",
      });
    } finally {
      setSaving(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("dm_admin_token");
    navigate("/admin/login");
  };

  // Accolade helpers
  const addAccolade = () =>
    setData((d) => ({
      ...d,
      accolades: [...d.accolades, { year: "", title: "", description: "" }],
    }));
  const updateAccolade = (i, k, v) =>
    setData((d) => ({
      ...d,
      accolades: d.accolades.map((a, idx) => (idx === i ? { ...a, [k]: v } : a)),
    }));
  const removeAccolade = (i) =>
    setData((d) => ({
      ...d,
      accolades: d.accolades.filter((_, idx) => idx !== i),
    }));

  // Gallery helpers
  const addPhoto = () =>
    setData((d) => ({ ...d, gallery: [...d.gallery, { src: "", caption: "" }] }));
  const updatePhoto = (i, k, v) =>
    setData((d) => ({
      ...d,
      gallery: d.gallery.map((g, idx) => (idx === i ? { ...g, [k]: v } : g)),
    }));
  const removePhoto = (i) =>
    setData((d) => ({ ...d, gallery: d.gallery.filter((_, idx) => idx !== i) }));

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-950 flex items-center justify-center text-neutral-400">
        Loading dashboard…
      </div>
    );
  }

  const inputCls =
    "bg-neutral-950 border-neutral-800 text-white h-11 rounded-sm focus-visible:ring-red-600";
  const taCls =
    "bg-neutral-950 border-neutral-800 text-white rounded-sm focus-visible:ring-red-600";
  const labelCls =
    "text-xs uppercase tracking-[0.25em] font-heading text-neutral-300";

  return (
    <div className="min-h-screen bg-neutral-950 text-white">
      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-black/85 backdrop-blur border-b border-neutral-800">
        <div className="max-w-7xl mx-auto px-5 md:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="flex items-center justify-center w-9 h-9 rounded-sm bg-red-600 text-white">
              <Shield className="w-4 h-4" />
            </span>
            <div>
              <div className="font-display text-lg tracking-wider">ADMIN</div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 font-heading">
                DaMarkas Portfolio
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a
              href="/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs uppercase tracking-[0.25em] text-neutral-400 hover:text-red-500 font-heading hidden sm:inline-flex items-center gap-1"
            >
              View Site <ExternalLink className="w-3 h-3" />
            </a>
            <Button
              onClick={save}
              disabled={saving}
              className="btn-primary bg-red-600 hover:bg-red-700 text-white font-heading uppercase tracking-widest rounded-sm h-9 px-4"
            >
              <Save className="w-4 h-4 mr-2" /> {saving ? "Saving..." : "Save"}
            </Button>
            <Button
              onClick={logout}
              variant="outline"
              className="bg-transparent border-neutral-700 text-white hover:bg-neutral-900 hover:text-white rounded-sm h-9 px-3"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 md:px-10 py-8">
        <Tabs defaultValue="profile" className="w-full">
          <TabsList className="bg-neutral-900 border border-neutral-800 rounded-sm flex flex-wrap h-auto p-1">
            <TabsTrigger value="profile" className="data-[state=active]:bg-red-600 data-[state=active]:text-white rounded-sm font-heading uppercase tracking-widest text-xs">
              Profile
            </TabsTrigger>
            <TabsTrigger value="stats" className="data-[state=active]:bg-red-600 data-[state=active]:text-white rounded-sm font-heading uppercase tracking-widest text-xs">
              Stats
            </TabsTrigger>
            <TabsTrigger value="accolades" className="data-[state=active]:bg-red-600 data-[state=active]:text-white rounded-sm font-heading uppercase tracking-widest text-xs">
              Accolades
            </TabsTrigger>
            <TabsTrigger value="film" className="data-[state=active]:bg-red-600 data-[state=active]:text-white rounded-sm font-heading uppercase tracking-widest text-xs">
              Film
            </TabsTrigger>
            <TabsTrigger value="gallery" className="data-[state=active]:bg-red-600 data-[state=active]:text-white rounded-sm font-heading uppercase tracking-widest text-xs">
              Gallery
            </TabsTrigger>
            <TabsTrigger value="inquiries" className="data-[state=active]:bg-red-600 data-[state=active]:text-white rounded-sm font-heading uppercase tracking-widest text-xs">
              Inquiries ({inquiries.length})
            </TabsTrigger>
          </TabsList>

          {/* PROFILE */}
          <TabsContent value="profile" className="mt-6 space-y-6">
            <Card title="Bio & Tagline">
              <div className="space-y-2">
                <Label className={labelCls}>Tagline</Label>
                <Input value={data.tagline} onChange={set("tagline")} className={inputCls} />
              </div>
              <div className="space-y-2 mt-4">
                <Label className={labelCls}>Bio</Label>
                <Textarea rows={6} value={data.bio} onChange={set("bio")} className={taCls} />
              </div>
            </Card>

            <Card title="Measurables & School">
              <div className="grid md:grid-cols-3 gap-4">
                <Field label="Height (Current)" v={data.heightCurrent} on={set("heightCurrent")} />
                <Field label="Height (Projected)" v={data.heightProjected} on={set("heightProjected")} />
                <Field label="Weight" v={data.weight} on={set("weight")} />
                <Field label="GPA" v={data.gpa} on={set("gpa")} />
                <Field label="Class Of" v={data.classOf} on={set("classOf")} />
                <Field label="Jersey #" v={data.jerseyNumber} on={set("jerseyNumber")} />
                <Field label="School" v={data.school} on={set("school")} />
                <Field label="Personal Email" v={data.contactEmail} on={set("contactEmail")} />
                <Field label="Recruiting Email" v={data.recruitingEmail} on={set("recruitingEmail")} />
              </div>
            </Card>
          </TabsContent>

          {/* STATS */}
          <TabsContent value="stats" className="mt-6 space-y-6">
            <Card title="Season Stats">
              <div className="space-y-2 mb-4">
                <Label className={labelCls}>Season Label</Label>
                <Input value={data.seasonLabel} onChange={set("seasonLabel")} className={inputCls} placeholder="e.g. 2025–2026 Season" />
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Field label="Sacks" type="number" v={data.sacks} on={set("sacks")} />
                <Field label="Tackles" type="number" v={data.tackles} on={set("tackles")} />
                <Field label="Forced Fumbles" type="number" v={data.forcedFumbles} on={set("forcedFumbles")} />
                <Field label="Fumble Recoveries" type="number" v={data.fumbleRecoveries} on={set("fumbleRecoveries")} />
              </div>
            </Card>
          </TabsContent>

          {/* ACCOLADES */}
          <TabsContent value="accolades" className="mt-6 space-y-6">
            <Card
              title="Accolades"
              right={
                <Button onClick={addAccolade} className="bg-red-600 hover:bg-red-700 text-white rounded-sm h-9 px-3">
                  <Plus className="w-4 h-4 mr-1" /> Add
                </Button>
              }
            >
              <div className="space-y-4">
                {data.accolades.map((a, i) => (
                  <div key={i} className="border border-neutral-800 rounded-sm p-4 bg-neutral-950">
                    <div className="grid md:grid-cols-12 gap-3">
                      <div className="md:col-span-2">
                        <Label className={labelCls}>Year</Label>
                        <Input
                          value={a.year}
                          onChange={(e) => updateAccolade(i, "year", e.target.value)}
                          className={inputCls + " mt-2"}
                        />
                      </div>
                      <div className="md:col-span-4">
                        <Label className={labelCls}>Title</Label>
                        <Input
                          value={a.title}
                          onChange={(e) => updateAccolade(i, "title", e.target.value)}
                          className={inputCls + " mt-2"}
                        />
                      </div>
                      <div className="md:col-span-5">
                        <Label className={labelCls}>Description</Label>
                        <Input
                          value={a.description}
                          onChange={(e) => updateAccolade(i, "description", e.target.value)}
                          className={inputCls + " mt-2"}
                        />
                      </div>
                      <div className="md:col-span-1 flex items-end">
                        <Button
                          onClick={() => removeAccolade(i)}
                          variant="outline"
                          className="bg-transparent border-neutral-700 text-red-500 hover:bg-red-600/10 hover:text-red-400 rounded-sm h-11 w-full"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
                {data.accolades.length === 0 && (
                  <div className="text-neutral-500 text-sm py-4 text-center">
                    No accolades yet. Click "Add" to create one.
                  </div>
                )}
              </div>
            </Card>
          </TabsContent>

          {/* FILM */}
          <TabsContent value="film" className="mt-6 space-y-6">
            <Card title="Highlight Video">
              <Field label="Video Title" v={data.highlightTitle} on={set("highlightTitle")} />
              <div className="mt-4">
                <Field
                  label="YouTube Video ID (the part after youtu.be/ or watch?v=)"
                  v={data.highlightYoutubeId}
                  on={set("highlightYoutubeId")}
                  placeholder="e.g. 89udWjXZ8II"
                />
              </div>
              {data.highlightYoutubeId && (
                <div className="mt-6 aspect-video rounded-sm overflow-hidden border border-neutral-800">
                  <iframe
                    className="w-full h-full"
                    src={`https://www.youtube.com/embed/${data.highlightYoutubeId}`}
                    title="preview"
                    frameBorder="0"
                    allowFullScreen
                  />
                </div>
              )}
            </Card>
          </TabsContent>

          {/* GALLERY */}
          <TabsContent value="gallery" className="mt-6 space-y-6">
            <Card
              title="Photo Gallery"
              right={
                <Button onClick={addPhoto} className="bg-red-600 hover:bg-red-700 text-white rounded-sm h-9 px-3">
                  <Plus className="w-4 h-4 mr-1" /> Add Photo
                </Button>
              }
            >
              <p className="text-xs text-neutral-500 mb-4">
                Paste a public image URL (from Google Drive shared link, Imgur, your existing site, etc.).
              </p>
              <div className="space-y-4">
                {data.gallery.map((g, i) => (
                  <div key={i} className="border border-neutral-800 rounded-sm p-4 bg-neutral-950">
                    <div className="grid md:grid-cols-12 gap-3 items-start">
                      <div className="md:col-span-2">
                        <div className="aspect-square w-full rounded-sm overflow-hidden border border-neutral-800 bg-neutral-900 flex items-center justify-center">
                          {g.src ? (
                            <img src={g.src} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <ImageIcon className="w-6 h-6 text-neutral-700" />
                          )}
                        </div>
                      </div>
                      <div className="md:col-span-9 space-y-3">
                        <div>
                          <Label className={labelCls}>Image URL</Label>
                          <Input
                            value={g.src}
                            onChange={(e) => updatePhoto(i, "src", e.target.value)}
                            className={inputCls + " mt-2"}
                            placeholder="https://..."
                          />
                        </div>
                        <div>
                          <Label className={labelCls}>Caption</Label>
                          <Input
                            value={g.caption}
                            onChange={(e) => updatePhoto(i, "caption", e.target.value)}
                            className={inputCls + " mt-2"}
                          />
                        </div>
                      </div>
                      <div className="md:col-span-1 flex items-start">
                        <Button
                          onClick={() => removePhoto(i)}
                          variant="outline"
                          className="bg-transparent border-neutral-700 text-red-500 hover:bg-red-600/10 hover:text-red-400 rounded-sm h-11 w-full"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
                {data.gallery.length === 0 && (
                  <div className="text-neutral-500 text-sm py-4 text-center">
                    No photos yet. Click "Add Photo" to create one.
                  </div>
                )}
              </div>
            </Card>
          </TabsContent>

          {/* INQUIRIES */}
          <TabsContent value="inquiries" className="mt-6 space-y-6">
            <Card title="Recent Inquiries" right={<span className="text-xs text-neutral-500">{inquiries.length} total</span>}>
              {inquiries.length === 0 ? (
                <div className="text-center py-12 text-neutral-500">
                  <Inbox className="w-10 h-10 mx-auto mb-3 opacity-50" />
                  No inquiries yet — when coaches/scouts submit the contact form, they'll show here.
                </div>
              ) : (
                <div className="space-y-3">
                  {inquiries.map((q) => (
                    <div key={q.id} className="border border-neutral-800 rounded-sm p-4 bg-neutral-950">
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div>
                          <div className="font-display text-lg text-white">{q.name}</div>
                          <a href={`mailto:${q.email}`} className="text-sm text-red-500 hover:underline inline-flex items-center gap-1">
                            <Mail className="w-3 h-3" /> {q.email}
                          </a>
                          {q.organization && (
                            <div className="text-xs text-neutral-400 mt-1 inline-flex items-center gap-1 ml-3">
                              <Briefcase className="w-3 h-3" /> {q.organization}
                            </div>
                          )}
                        </div>
                        <div className="text-right">
                          <span className="text-[10px] uppercase tracking-[0.25em] font-heading text-red-500 border border-red-500/40 rounded-sm px-2 py-1">
                            {q.inquiryType}
                          </span>
                          <div className="text-[10px] text-neutral-500 mt-2">
                            {new Date(q.createdAt).toLocaleString()}
                          </div>
                        </div>
                      </div>
                      <p className="mt-3 text-neutral-300 text-sm whitespace-pre-wrap">{q.message}</p>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

const Card = ({ title, right, children }) => (
  <section className="border border-neutral-800 bg-neutral-900/50 rounded-sm p-6">
    <header className="flex items-center justify-between mb-5 gap-3 flex-wrap">
      <div className="flex items-center gap-3">
        <span className="accent-bar" />
        <h2 className="font-display text-2xl text-white tracking-wider">{title}</h2>
      </div>
      {right}
    </header>
    {children}
  </section>
);

const Field = ({ label, v, on, type = "text", placeholder }) => (
  <div className="space-y-2">
    <Label className="text-xs uppercase tracking-[0.25em] font-heading text-neutral-300">
      {label}
    </Label>
    <Input
      type={type}
      value={v ?? ""}
      onChange={on}
      placeholder={placeholder}
      className="bg-neutral-950 border-neutral-800 text-white h-11 rounded-sm focus-visible:ring-red-600"
    />
  </div>
);

export default AdminDashboard;
